import React, { useEffect, useState } from "react";

/**
 * The text way into image search: "malibu industrial warehouse".
 *
 * Renders in two places (KTD7): above the drop zone on the landing view, and
 * in the results toolbar during a text search — the same component, so the
 * phrase is always editable in place rather than retype-from-scratch.
 *
 * The bar is disabled while a text search is in flight: a submit is a
 * discrete action with no debounce, and a double-Enter against a 5-credit
 * endpoint must not fire twice.
 */
export default function SearchBar({ initial, disabled, onSubmit, compact }) {
    const [draft, setDraft] = useState(initial || "");

    // A replay or Back/Forward can change the active query underneath the
    // bar; follow it so the bar always shows the search on screen. Local
    // edits still win between submits (state only resets when `initial`
    // itself changes).
    useEffect(() => {
        setDraft(initial || "");
    }, [initial]);

    const submit = (e) => {
        e.preventDefault();
        const phrase = draft.replace(/\s+/g, " ").trim();
        // Whitespace-only is a no-op at this layer too — no request, no URL.
        if (!phrase || disabled) return;
        onSubmit(phrase);
    };

    return (
        <form
            className={"il-search-bar d-flex" + (compact ? " il-search-bar-compact" : "")}
            onSubmit={submit}
        >
            <input
                type="text"
                className="form-control"
                placeholder="Describe what you're looking for — e.g. “malibu industrial warehouse”"
                aria-label="Describe what you're looking for"
                value={draft}
                disabled={disabled}
                maxLength={200}
                onChange={(e) => setDraft(e.target.value)}
            />
            <button type="submit" className="btn btn-primary ml-2" disabled={disabled}>
                Search
            </button>
        </form>
    );
}

/**
 * Lift a place word out of the phrase (KTD5): scan against the city and
 * county facet lists the page already ships for the dropdowns — longest
 * word-boundary match wins, case-insensitive. Looks, themes, and vibes are
 * deliberately NOT detected; they are Ximilar's job, not our tagging's.
 *
 * Returns { q, city, county } — q with the matched words stripped, so
 * "malibu industrial warehouse" searches "industrial warehouse" filtered to
 * Malibu.
 */
export function detectPlace(phrase, facets) {
    const scan = (values) => {
        let best = null;
        (values || []).forEach((value) => {
            if (!value) return;
            const pattern = new RegExp(
                "\\b" + String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "\\b",
                "i"
            );
            if (pattern.test(phrase) && (!best || value.length > best.length)) {
                best = value;
            }
        });
        return best;
    };

    const city = scan(facets && facets.cities);
    // County only when no city matched — one place chip, and a city is the
    // more specific signal.
    const county = city ? null : scan(facets && facets.counties);
    const matched = city || county;

    let q = phrase;
    if (matched) {
        q = phrase
            .replace(new RegExp("\\b" + matched.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "\\b", "i"), " ")
            .replace(/\s+/g, " ")
            .trim();
        // A phrase that WAS just the place word ("malibu") keeps it as the
        // query too — an empty text query has nothing to search.
        if (!q) {
            return { q: phrase, city: null, county: null };
        }
    }

    return { q, city: city || null, county: county || null };
}
